import * as React from "react"
import { RouteComponentProps, WindowLocation } from "@gatsbyjs/reach-router"
import useProvisioningOptions from "@modules/provisioning/hooks/useProvisioningOptions"
import { StandardSingleColumn } from "@modules/ui/layouts/Containers"
import { ProvisionConfigurationHeader } from "@modules/site/create/Provision/components/ProvisionConfigurationHeader"
import { ProvisionConfigurationAside } from "@modules/site/create/Provision/components/ProvisionConfigurationAside"
import { ProvisionBasicConfiguration } from "@modules/site/create/Provision/components/ProvisionBasicConfiguration"
import { ProvisionCmsIntegrationCard } from "@modules/site/create/Provision/components/ProvisionCmsIntegrationCard"
import { ProvisionSummary } from "@modules/site/create/Provision/components/ProvisionSummary"

import {
  Button,
  Card,
  CardHeader,
  CardSection,
  Spacer,
  Text,
} from "gatsby-interface"
import { CmsVendor } from "@modules/graphql/types"
import { provisionSite } from "@modules/locales/default"
import { getPathToSiteDetails } from "@modules/site/details/utils"
import { navigate } from "gatsby"
import useCreateSiteState, {
  CreateSiteType,
  ProvisionConfigurationStep,
} from "@modules/site/create/shared/hooks/useCreateSiteState"

type SiteConfigurationProps = RouteComponentProps<{
  organizationId: string
}>

export function ProvisionConfiguration({
  organizationId = "",
  location = {} as WindowLocation,
}: SiteConfigurationProps) {
  const { step, setStep, siteId, setSiteId } = useCreateSiteState(
    CreateSiteType.Provision
  )

  const stepLabels = [
    "Create a Git repository",
    "Connect Integrations",
    "Summary",
  ]

  const {
    provisioningOptions,
    verifyProvisioningOptions,
  }: {
    provisioningOptions: {
      starter: string
      starterImageUrl: string
      starterTitle: string
      starterType: string
      cmsProvider: CmsVendor
      cmsProviderFormattedName: string
      cmsTemplateId: string
      cmsProviderLogo: string
    }
    verifyProvisioningOptions: (options: {
      includeStarterProperties: boolean
    }) => void
  } = useProvisioningOptions(organizationId)

  // Make sure we have all provisioning information in session
  verifyProvisioningOptions({ includeStarterProperties: true })

  const noCmsProvider = provisioningOptions.cmsProvider === `NONE`

  if (noCmsProvider) {
    // Remove CMS Integrations label in sidebar if we won't perform that step
    stepLabels.splice(1, 1)
  }

  return (
    <StandardSingleColumn
      css={theme => ({
        display: `grid`,
        gridTemplateColumns: `3fr 1fr`,
        gridTemplateAreas: `"header header"
                          "main main"`,
        gap: theme.space[10],
        marginTop: theme.space[8],
        paddingBottom: "3rem",

        [theme.mediaQueries.tablet]: {
          gridTemplateAreas: `"header header"
                            "main aside"`,
          paddingBottom: "7.5rem",
        },
      })}
    >
      <ProvisionConfigurationHeader
        css={{ gridArea: `header` }}
        organizationId={organizationId}
        location={location}
        starterName={provisioningOptions.starter}
      />
      <main css={{ gridArea: `main` }}>
        <Card>
          <CardHeader
            // not ideal, but how we need to override font family to be Inter
            css={theme => ({
              h3: {
                fontFamily: theme.fonts.heading,
              },
            })}
            title="Create a Git repository"
          />
          <CardSection>
            <Text>{provisionSite.messages.creatingRepository}</Text>
            <ProvisionBasicConfiguration
              organizationId={organizationId}
              starterName={provisioningOptions.starter}
              starterVendor={provisioningOptions.cmsProvider}
              isCurrentStep={
                step === ProvisionConfigurationStep.BasicConfiguration
              }
              onSiteCreated={(newSiteId: string) => {
                const nextStep = noCmsProvider
                  ? ProvisionConfigurationStep.ConfigurationComplete
                  : ProvisionConfigurationStep.ConnectIntegrations
                setSiteId(newSiteId)
                setStep(nextStep)
              }}
            />
          </CardSection>
        </Card>
        {noCmsProvider ? null : (
          <React.Fragment>
            <Spacer size={8} />
            <ProvisionCmsIntegrationCard
              siteId={siteId}
              starterVendor={provisioningOptions.cmsProvider}
              starterCmsTemplateId={provisioningOptions.cmsTemplateId}
              starterCmsProviderFormattedName={
                provisioningOptions.cmsProviderFormattedName
              }
              onNext={() => {
                setStep(ProvisionConfigurationStep.ConfigurationComplete)
              }}
            />
          </React.Fragment>
        )}
        <Spacer size={8} />
        {step === ProvisionConfigurationStep.ConfigurationComplete &&
        organizationId &&
        siteId ? (
          <ProvisionSummary organizationId={organizationId} siteId={siteId} />
        ) : null}
        <Spacer size={8} />

        <Button
          data-ld="create-site"
          onClick={() => {
            navigate(
              `${getPathToSiteDetails(siteId, organizationId)}?newsite=true`
            )
          }}
          disabled={
            !siteId || step !== ProvisionConfigurationStep.ConfigurationComplete
          }
        >
          {`View your new site`}
        </Button>
      </main>
      <ProvisionConfigurationAside
        css={theme => ({
          gridArea: `aside`,
          display: `none`,
          [theme.mediaQueries.tablet]: { display: `block` },
        })}
        currentStep={step}
        starterImageUrl={provisioningOptions.starterImageUrl}
        starterTitle={provisioningOptions.starterTitle}
        starterVendor={provisioningOptions.cmsProvider}
        starterCmsTemplateId={provisioningOptions.cmsTemplateId}
        stepLabels={stepLabels}
      />
    </StandardSingleColumn>
  )
}
